import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Controller } from 'react-hook-form';
import {
  FaArrowRight,
  FaCheckCircle,
  FaDownload,
  FaEnvelope,
  FaExclamationCircle,
  FaEye,
  FaFileInvoiceDollar,
  FaRegFileAlt,
} from 'react-icons/fa';
import CommonReactSelect from 'components/reactSelectCommon/CommonReactSelect';
import MerchantFilterDropdown from 'components/MerchantFilterDropdown';
import useMerchantLabelFilter from 'hooks/useMerchantLabelFilter';
import {
  YearOptionsForInvoice,
  QuarterOptionsForInvoice,
  selectDaysOptions,
  monthsSelectionsOptions,
  INVOICE_LEGAL_SCOPES,
  INVOICE_ACTIONS,
} from 'utils/StaticData';
import PropTypes from 'prop-types';
import {
  FieldLabel,
  InvoiceActionPicker,
  SegmentedTabs,
} from './InvoiceActionPicker';
import InvoiceConfirmationDialog from './InvoiceConfirmationDialog';

const SCOPE_TABS = [
  {
    value: INVOICE_LEGAL_SCOPES.COMPLETE,
    label: 'Complete Legal Info',
    icon: FaCheckCircle,
    iconColor: 'reviveGreen',
  },
  {
    value: INVOICE_LEGAL_SCOPES.INCOMPLETE,
    label: 'Incomplete Legal Info',
    icon: FaExclamationCircle,
    iconColor: 'red.500',
  },
];

const PERIOD_TABS = selectDaysOptions.map((option) => ({
  value: String(option.id),
  label: option.label,
}));

const [QUARTER_PERIOD, MONTH_PERIOD] = selectDaysOptions;

const InvoiceGenerationModal = ({
  isOpen,
  onClose,
  handleSubmit,
  register,
  control,
  watch,
  reset,
  clearErrors,
  setValue,
  errors,
  invoiceMerchantFilterHook,
  modalMerchants,
  handleGenerate,
  isPDFDownload,
  isEmailSending,
  isGenerating,
  isFormIncomplete,
  isEmailActionAvailable,
  languageOptions,
}) => {
  const scope = watch('legalInfoScope') ?? INVOICE_LEGAL_SCOPES.COMPLETE;
  const selectedAction = watch('invoiceAction') ?? '';
  const selectedMerchants = watch('merchant') ?? [];
  const isQuarterPeriod = watch('daysSelect')?.id === QUARTER_PERIOD.id;

  // Only the merchants in the chosen legal-info state, so a selection can never
  // mix the two - they follow different invoicing rules.
  const isIncompleteScope = scope === INVOICE_LEGAL_SCOPES.INCOMPLETE;
  const scopedMerchants = modalMerchants.filter(
    (merchant) => !!merchant.legalInfoComplete !== isIncompleteScope,
  );

  const officialActionState = (label) =>
    isIncompleteScope
      ? {
          isDisabled: true,
          disabledReason: `${label} is unavailable until the merchant's legal info is complete.`,
          disabledHint: 'Requires legal information',
        }
      : {};

  const ACTIONS = [
    {
      value: INVOICE_ACTIONS.PREVIEW,
      label: 'Preview Invoice',
      description: 'Unofficial copy, nothing is issued',
      icon: FaEye,
    },
    {
      value: INVOICE_ACTIONS.GENERATE,
      label: 'Generate Invoice',
      description: 'Create and store the invoice',
      icon: FaRegFileAlt,
      ...officialActionState('Generating'),
    },
    {
      value: INVOICE_ACTIONS.DOWNLOAD,
      label: 'Download Invoice',
      description: 'Download as PDF',
      icon: FaDownload,
      ...officialActionState('Downloading'),
    },
    {
      value: INVOICE_ACTIONS.EMAIL,
      label: 'Send Email',
      description: 'Send to the merchant',
      icon: FaEnvelope,
      // RAT-1712: emailing stays switched off until it is deliberately re-enabled.
      ...(isEmailActionAvailable
        ? officialActionState('Emailing')
        : {
            isDisabled: true,
            disabledReason: 'Emailing invoices is temporarily switched off.',
            disabledHint: 'Temporarily switched off',
          }),
    },
  ];

  const isBusy = isGenerating || isPDFDownload || isEmailSending;
  const canProceed =
    selectedMerchants.length > 0 && !!selectedAction && !isFormIncomplete;

  // RAT-1828: every action is confirmed before it runs - Preview included - so
  // the period and merchant set are always restated back before anything starts.
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // This modal stays mounted while closed, so the labels are fetched when it
  // actually opens rather than on mount. The backend scopes the list and each
  // merchantCount to the caller's role.
  const merchantLabelsHook = useMerchantLabelFilter();
  const { fetchLabels: fetchMerchantLabels } = merchantLabelsHook;
  useEffect(() => {
    if (isOpen) fetchMerchantLabels();
  }, [isOpen, fetchMerchantLabels]);

  const runSelectedAction = () => handleGenerate(selectedAction)();

  // Validation runs before the confirmation opens, so what it restates is
  // always a configuration that can actually be submitted.
  const onProceed = handleSubmit(() => setIsConfirmOpen(true));

  const onConfirm = () => {
    runSelectedAction();
    setIsConfirmOpen(false);
  };

  const selectedYear = watch('year')?.label ?? '';
  const fromMonth = watch('month');
  const toMonth = watch('endMonth');
  const periodLabel = isQuarterPeriod
    ? [watch('quarter')?.label, selectedYear].filter(Boolean).join(' ')
    : [
        fromMonth?.label && toMonth?.label && toMonth.id !== fromMonth.id
          ? `${fromMonth.label} - ${toMonth.label}`
          : fromMonth?.label,
        selectedYear,
      ]
        .filter(Boolean)
        .join(' ');

  // Scope is read from the selection itself: covering every merchant listed for
  // the current legal-info state means "All", anything less is a subset.
  const isAllScoped =
    scopedMerchants.length > 0 &&
    selectedMerchants.length === scopedMerchants.length;
  const scopeLabel = isAllScoped
    ? `All merchants (${scopedMerchants.length})`
    : `Selected merchants (${selectedMerchants.length} of ${scopedMerchants.length})`;

  const onScopeChange = (nextScope) => {
    setValue('legalInfoScope', nextScope);
    // The merchants listed change with the state, so a stale selection would be
    // invisible but still submitted; the same goes for an action that is no
    // longer allowed. Active label chips go with it - they describe a selection
    // that no longer exists.
    setValue('merchant', []);
    merchantLabelsHook.clearSelectedLabels();
    if (nextScope === INVOICE_LEGAL_SCOPES.INCOMPLETE) {
      setValue('invoiceAction', INVOICE_ACTIONS.PREVIEW);
    }
  };

  const onPeriodChange = (nextPeriod) => {
    const isQuarter = nextPeriod === String(QUARTER_PERIOD.id);
    setValue('daysSelect', isQuarter ? QUARTER_PERIOD : MONTH_PERIOD);
    if (isQuarter) {
      setValue('month', null);
      setValue('endMonth', null);
      clearErrors(['month', 'endMonth']);
    } else {
      setValue('quarter', null);
      clearErrors('quarter');
    }
  };

  const closeAndReset = () => {
    setIsConfirmOpen(false);
    reset();
    clearErrors();
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        scrollBehavior="inside"
        closeOnOverlayClick={false}
        size={{ base: 'sm', md: 'xl', lg: '3xl' }}
      >
        <ModalOverlay />
        <ModalContent
          mx={{ base: 3, md: 4 }}
          my={{ base: 4, md: 8 }}
          maxH={{ base: '92vh', md: '88vh' }}
          borderRadius={{ base: '12px', md: '16px' }}
        >
          <ModalHeader display="flex" gap={3} alignItems="flex-start" pb={3}>
            <Icon
              as={FaFileInvoiceDollar}
              color="primary"
              w="22px"
              h="22px"
              mt="2px"
            />
            <Box>
              <Text fontSize="lg" fontWeight="700">
                Generate Invoices
              </Text>
              {/* <Text fontSize="sm" fontWeight="400" color="gray.500">
                Select details and choose an action to proceed.
              </Text> */}
            </Box>
          </ModalHeader>
          <ModalCloseButton onClick={closeAndReset} />
          <ModalBody pt={0} px={{ base: 4, md: 6 }} overflowY="auto">
            <VStack spacing={4} align="stretch">
              {/* RAT-1826: the legal-info state comes first - it decides which
                  merchants are listed and which actions are possible. */}
              <SegmentedTabs
                options={SCOPE_TABS}
                value={scope}
                onChange={onScopeChange}
              />

              {isIncompleteScope && (
                <Alert status="error" borderRadius="md" py={2}>
                  <AlertIcon />
                  <Text fontSize="sm">
                    Legal info incomplete. Only a preview invoice can be
                    generated until the merchant&apos;s legal info is complete.
                  </Text>
                </Alert>
              )}

              <Box>
                <FieldLabel>Merchants</FieldLabel>
                <MerchantFilterDropdown
                  searchTerm={invoiceMerchantFilterHook.searchTerm}
                  setSearchTerm={invoiceMerchantFilterHook.setSearchTerm}
                  selectedMerchantText={invoiceMerchantFilterHook.getSelectedMerchantText()}
                  isPopoverOpen={
                    invoiceMerchantFilterHook.isMerchantPopoverOpen
                  }
                  openPopover={invoiceMerchantFilterHook.openMerchantPopover}
                  closePopover={invoiceMerchantFilterHook.closeMerchantPopover}
                  isDisabled={false}
                  merchantNameList={scopedMerchants}
                  filteredMerchants={invoiceMerchantFilterHook.getFilteredMerchants(
                    scopedMerchants,
                  )}
                  pendingSelectedMerchant={
                    invoiceMerchantFilterHook.pendingSelectedMerchant
                  }
                  onSelectAll={invoiceMerchantFilterHook.handleSelectAll}
                  onMerchantToggle={
                    invoiceMerchantFilterHook.handleMerchantToggle
                  }
                  isSelectAllChecked={
                    invoiceMerchantFilterHook.isSelectAllChecked
                  }
                  isMerchantChecked={
                    invoiceMerchantFilterHook.isMerchantChecked
                  }
                  onApply={invoiceMerchantFilterHook.handleApplySelection}
                  onCancel={invoiceMerchantFilterHook.handleCancelSelection}
                  onClearSelection={() => {
                    invoiceMerchantFilterHook.handleClearSelection();
                    merchantLabelsHook.clearSelectedLabels();
                  }}
                  merchantGroups={[]}
                  selectedGroupIds={[]}
                  isGroupsLoading={false}
                  onGroupSelection={() => {}}
                  onCreateGroup={() => {}}
                  onEditGroup={() => {}}
                  showGroups={false}
                  showLabels
                  merchantLabels={merchantLabelsHook.labels}
                  selectedLabelIds={merchantLabelsHook.selectedLabelIds}
                  isLabelsLoading={merchantLabelsHook.isFetchingLabels}
                  onLabelSelection={(label) =>
                    merchantLabelsHook.handleLabelSelection(
                      label,
                      invoiceMerchantFilterHook.pendingSelectedMerchant,
                      invoiceMerchantFilterHook.setPendingSelectedMerchant,
                      // Only the merchants in the chosen legal-info state - a
                      // chip must never pull in the other scope's merchants.
                      scopedMerchants,
                    )
                  }
                  showSearch={true}
                  showSelectAll={true}
                  maxHeight="420px"
                  buttonWidth="100%"
                />
              </Box>

              <Box>
                <FieldLabel>Time Period</FieldLabel>
                <SegmentedTabs
                  options={PERIOD_TABS}
                  value={String(watch('daysSelect')?.id ?? QUARTER_PERIOD.id)}
                  onChange={onPeriodChange}
                />
              </Box>

              {/* Year and Language apply to both period shapes, so they hold this
                  first row in either mode and only the row below them changes. */}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <Controller
                  name="year"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CommonReactSelect
                      label="Year"
                      name="year"
                      type="select"
                      options={YearOptionsForInvoice}
                      value={value ? [value] : []}
                      onChange={onChange}
                      register={register}
                      error={errors?.year}
                      getOptionLabel={(e) => e?.label}
                      getOptionValue={(e) => e?.id}
                      placeholderValue="Year"
                    />
                  )}
                />
                {/* Language keeps this slot in both period modes, so switching
                    between Quarters and Months never moves it. */}
                <Controller
                  name="language"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CommonReactSelect
                      label="Language"
                      name="language"
                      type="select"
                      options={languageOptions}
                      value={value ? [value] : []}
                      onChange={onChange}
                      register={register}
                      error={errors?.language}
                      getOptionLabel={(e) => e?.label}
                      getOptionValue={(e) => e?.id}
                      placeholderValue="Language"
                    />
                  )}
                />
              </SimpleGrid>

              {/* The period row below: one full-width Quarter, or From/To month
                  side by side. Both modes therefore keep the same two rows. */}
              {isQuarterPeriod && (
                <Controller
                  name="quarter"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CommonReactSelect
                      label="Quarter"
                      name="quarter"
                      type="select"
                      options={QuarterOptionsForInvoice}
                      value={value ? [value] : []}
                      onChange={onChange}
                      register={register}
                      isRequired={true}
                      error={errors?.quarter}
                      getOptionLabel={(e) => e?.label}
                      getOptionValue={(e) => e?.id}
                      placeholderValue="Select quarter"
                    />
                  )}
                />
              )}

              {!isQuarterPeriod && (
                <Box>
                  {/* RAT-1610: at most 6 consecutive months, within one year. */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <Controller
                      name="month"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <CommonReactSelect
                          label="From Month"
                          name="month"
                          type="select"
                          options={monthsSelectionsOptions}
                          value={value ? [value] : []}
                          onChange={(selected) => {
                            onChange(selected);
                            const to = watch('endMonth');
                            if (
                              selected?.id &&
                              (!to?.id ||
                                to.id < selected.id ||
                                to.id > selected.id + 5)
                            ) {
                              setValue('endMonth', selected);
                              clearErrors('endMonth');
                            }
                          }}
                          register={register}
                          isRequired={true}
                          error={errors?.month}
                          getOptionLabel={(e) => e?.label}
                          getOptionValue={(e) => e?.id}
                          placeholderValue="Select from month"
                        />
                      )}
                    />
                    <Controller
                      name="endMonth"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <CommonReactSelect
                          label="To Month"
                          name="endMonth"
                          type="select"
                          options={monthsSelectionsOptions.filter(
                            (m) =>
                              !watch('month')?.id ||
                              (m.id >= watch('month').id &&
                                m.id <= watch('month').id + 5),
                          )}
                          value={value ? [value] : []}
                          onChange={onChange}
                          register={register}
                          isRequired={true}
                          isDisabled={!watch('month')?.id}
                          error={errors?.endMonth}
                          getOptionLabel={(e) => e?.label}
                          getOptionValue={(e) => e?.id}
                          placeholderValue="Select to month"
                        />
                      )}
                    />
                  </SimpleGrid>
                </Box>
              )}

              <InvoiceActionPicker
                actions={ACTIONS}
                value={selectedAction}
                onChange={(action) => setValue('invoiceAction', action)}
              />
            </VStack>
          </ModalBody>
          <ModalFooter
            gap={3}
            flexDirection={{ base: 'column-reverse', sm: 'row' }}
            px={{ base: 4, md: 6 }}
          >
            <Button
              type="button"
              variant="outline"
              fontSize="sm"
              fontWeight="500"
              minW="120px"
              w={{ base: '100%', sm: 'auto' }}
              onClick={closeAndReset}
            >
              Cancel
            </Button>
            <Button
              type="button"
              bg="primary"
              color="white"
              _hover={{ bg: 'primary', opacity: 0.8 }}
              rightIcon={<Icon as={FaArrowRight} w="13px" h="13px" />}
              isLoading={isBusy}
              loadingText="Starting..."
              isDisabled={!canProceed || isBusy}
              fontSize="sm"
              fontWeight="500"
              minW="160px"
              w={{ base: '100%', sm: 'auto' }}
              onClick={onProceed}
            >
              Proceed
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Stacked on top of the dialog above, so cancelling it drops the user back
        into the configuration exactly as they left it. */}
      {isOpen && isConfirmOpen && (
        <InvoiceConfirmationDialog
          isOpen={isConfirmOpen}
          onCancel={() => setIsConfirmOpen(false)}
          onConfirm={onConfirm}
          isBusy={isBusy}
          actionLabel={
            ACTIONS.find((action) => action.value === selectedAction)?.label ??
            ''
          }
          periodLabel={periodLabel || 'N/A'}
          languageLabel={watch('language')?.label ?? 'N/A'}
          scopeLabel={scopeLabel}
          merchantStatusLabel={
            SCOPE_TABS.find((tab) => tab.value === scope)?.label ?? ''
          }
          merchants={selectedMerchants}
        />
      )}
    </>
  );
};

InvoiceGenerationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  control: PropTypes.object.isRequired,
  watch: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  invoiceMerchantFilterHook: PropTypes.object.isRequired,
  modalMerchants: PropTypes.array.isRequired,
  handleGenerate: PropTypes.func.isRequired,
  isPDFDownload: PropTypes.bool.isRequired,
  isEmailSending: PropTypes.bool.isRequired,
  isGenerating: PropTypes.bool.isRequired,
  isFormIncomplete: PropTypes.bool.isRequired,
  isEmailActionAvailable: PropTypes.bool,
  languageOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default InvoiceGenerationModal;
